import env from '@/config/env';
import SocketService from '@/services/socket.io';
import { setAuthority } from '@/utils/infrastructure/auth/authority';
import {
  SESSION_TYPES,
  STORAGE_KEYS,
  beginContextTransition,
  getCurrentSession,
  getSessionItem,
  isAppPath,
  isSameContextId,
  persistAutomyTenantToken,
  removeSessionItem,
  setSessionItem,
  subscribeToSessionContextChange,
} from '@/utils/infrastructure/auth';
import {
  bootstrapAuthenticatedWhiteLabel,
  resetWhiteLabelToDefault,
} from '@/utils/infrastructure/whiteLabel';
import { notification } from 'antd';
import jwtDecode from 'jwt-decode';
import { getIntl, history } from 'umi';
import { buildPortalPath } from '@/utils/infrastructure/utilities';
import {
  queryAllowedEnvironments,
  queryAllowedSites,
  queryChangeCompany,
  queryChangeEnvironment,
  queryCompanies,
} from './service';

/**
 * A tab shows its tenant in two places: the profile (`user.currentUser`) and
 * the context bar (`userContext.current*`). The tenant only counts as applied
 * when every one that is known already matches.
 */
const differsFromAny = (knownIds, id) => {
  const known = knownIds.filter(Boolean);
  return !known.length || known.some((knownId) => !isSameContextId(knownId, id));
};

const isInTwoFactorFlow = () => {
  try {
    const state = getSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
    return state ? !!JSON.parse(state).required : false;
  } catch (e) {
    return false;
  }
};

/**
 * `changeContext` writes the new company/environment onto AUTH_USER in storage,
 * but Redux `user.currentUser` only updates when `user/fetchCurrent` finishes.
 * Chat (BuilderChat remount key, SupportChat context cleanup) reads from Redux,
 * so without this sync it keeps requesting threads of the outgoing tenant with
 * the new token → 404 on `GET /chat/thread/{id}`.
 */
const readSessionAuthUser = () => {
  try {
    return JSON.parse(getSessionItem(STORAGE_KEYS.AUTH_USER) || 'null');
  } catch (e) {
    return null;
  }
};

/**
 * Routes served by their own shell (portals, embedded apps) resolve their tenant
 * from the URL, so sending them to the Automy home would eject the user.
 */
const isForeignShellRoute = () => {
  const pathname = window.location?.pathname || '';
  return pathname.startsWith('/p/') || isAppPath();
};

/** The socket authenticated with the previous token and joined its rooms. */
const reconnectSocket = () => {
  try {
    SocketService.getInstance().reconnectWithNewToken(env.SOCKET_CONNECTION_HUB);
  } catch (e) {}
};

const selectOpenPicker = (state) => state.userContext.searchContext;

const notifyContextAdopted = (contextKey, name) => {
  try {
    const { formatMessage } = getIntl();
    notification.info({
      key: 'context-sync',
      message: formatMessage({ id: 'global.text.context.synced.title' }),
      // The tenant name comes from the token claims; on the rare token that
      // omits it the title alone still explains why the tab moved.
      ...(name && {
        description: formatMessage({ id: `global.text.context.synced.${contextKey}` }, { name }),
      }),
      duration: 8,
    });
  } catch (e) {}
};

const initialState = {
  data: [],
  sites: [],
  latestFetchCompanies: null,
  latestFetchAllowedEnvironments: null,
  userInfoCompany: null,
  searchCriteria: '',
  currentEnvironment: '',
  currentCompany: '',
  searchContext: false,
};

const Model = {
  namespace: 'userContext',
  state: initialState,
  effects: {
    *fetchContextData({ payload }, { call, put }) {
      try {
        const res = yield call(queryCompanies, payload);
        yield put({ type: 'queryContextData', payload: res });
      } catch (e) {}
    },

    *fetchCompanies({ payload }, { call, put, select }) {
      try {
        const res = yield call(queryCompanies, payload);
        // The picker may have closed or moved to another list meanwhile; its
        // items would then be offered as companies.
        if ((yield select(selectOpenPicker)) !== 'company') return;
        yield put({ type: 'queryContextData', payload: res });
      } catch (e) {}
    },

    *fetchAllowedEnvironments({ payload }, { call, put, select }) {
      try {
        const res = yield call(queryAllowedEnvironments, payload);
        if (_.isArray(res) && (yield select(selectOpenPicker)) !== 'environment') return;
        yield put({
          type: 'queryContextData',
          payload: _.isArray(res) ? res : { context: 'environment', value: res > 1 },
        });
      } catch (e) {}
    },

    *fetchAllowedSites({ payload }, { call, put }) {
      try {
        const { items, totalCount } = yield call(queryAllowedSites, payload);
        yield put({
          type: 'queryContextData',
          payload: { context: 'site', value: !!totalCount, items },
        });
      } catch (e) {}
    },

    *allowedSites({ payload }, { put, select }) {
      let data = yield select((state) => state.userContext.sites);
      if (payload.search) {
        data = _.filter(
          data,
          (o) =>
            _.toLower(o.name).includes(payload.search) ||
            _.toLower(o.shortCode).includes(payload.search),
        );
      }
      yield put({ type: 'queryContextData', payload: data });
    },

    *changeCompany({ payload }, { call, put }) {
      try {
        const res = yield call(queryChangeCompany, payload);
        // Anything still in flight for the outgoing company will be rejected.
        beginContextTransition();
        yield put({ type: 'changeContext', payload: { ...res, context: 'company' } });

        // Update authority after reducer completes to avoid Redux violations
        try {
          const user = jwtDecode(res.accessToken);
          setAuthority(Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles));
        } catch (e) {}

        // Point Redux at the new tenant before any chat/socket work below or in
        // onFinish runs, so remount keys and context-change effects see the switch.
        const authUser = readSessionAuthUser();
        if (authUser) {
          yield put({ type: 'user/saveCurrentUser', payload: authUser });
        }

        // The brand is tenant-scoped: drop the outgoing one before resolving the
        // incoming one, so the new company is never shown with the previous mark.
        resetWhiteLabelToDefault({ updateTitle: false });
        bootstrapAuthenticatedWhiteLabel({ updateTitle: false });

        yield put({ type: 'chat/resetChatState' });
        // Clear any tenant-scoped caches so the next render refetches fresh data.
        // - `processes/resetLaunchpadHome`: process Launchpad view (own slice).
        // - `launchpad/resetAll`: unified Launchpad model that serves the
        //   Lists, Reports, Agents, Apps, Connections and Entities views.
        //   Without this they keep showing the previous tenant's items
        //   until the user manually refreshes.
        yield put({ type: 'processes/resetLaunchpadHome' });
        yield put({ type: 'launchpad/resetAll' });
        yield put({ type: 'dashboard/reset' });
        yield put({ type: 'processes/saveFavoritesQuick', payload: [] });
        // Only now that the new token is stored: these must describe the
        // incoming company, not the one that was just left.
        yield put({ type: 'fetchAllowedEnvironments', payload: { count: true } });
        yield put({ type: 'fetchAllowedSites' });
        yield put({ type: 'global/tasksCountPending' });
        yield put({ type: 'sessionNavigation/fetchNavigation' });
        yield put({ type: 'channelOnboarding/invalidateOnboardingCache' });

        // Every entry point (context picker, "switch company" toasts) needs
        // these, so they live here rather than in each caller's onFinish.
        reconnectSocket();
        yield put({ type: 'user/fetchCurrent', payload: true });

        payload.onFinish?.();
      } catch (e) {}
    },

    /**
     * Adopt the tenant carried by the token another tab just wrote.
     *
     * Every tab reads the same token from localStorage, so a switch anywhere
     * re-points all of them at once at the HTTP level. The tabs that did not
     * trigger it keep their previous tenant in Redux and keep asking for its
     * records — the support chat being the loudest, since it holds an open
     * thread id and the API answers that no such thread exists. Realigning the
     * whole tab is the only state that is consistent with the token it is
     * already sending.
     *
     * Runs on any token write, so it must stay a no-op when the tenant did not
     * actually change (token refreshes rewrite the same entry).
     */
    *adoptExternalContext({ payload }, { put, select }) {
      const { companyId, companyName, environmentId, environmentName, roles } = payload || {};
      if (!companyId) return;

      const current = yield select((state) => ({
        userId: state.user?.currentUser?.id,
        companyIds: [state.user?.currentUser?.company?.id, state.userContext?.currentCompany?.id],
        environmentIds: [
          state.user?.currentUser?.defaultEnvironment?.id,
          state.userContext?.currentEnvironment?.id,
        ],
      }));

      // The shell is still booting or authenticating: it will read the fresh
      // token by itself and has no stale tenant state to correct yet.
      if (!current.userId) return;

      // Judging by the profile alone let a tab skip a switch it never applied
      // once its profile had drifted onto the incoming tenant.
      const companyChanged = differsFromAny(current.companyIds, companyId);
      const environmentChanged =
        !!environmentId && differsFromAny(current.environmentIds, environmentId);
      if (!companyChanged && !environmentChanged) return;

      beginContextTransition();

      yield put({
        type: 'adoptContext',
        payload: { companyId, companyName, environmentId, environmentName },
      });

      if (Array.isArray(roles) && roles.length) setAuthority(roles);

      // Mirror the adopted tenant onto Redux immediately (fetchCurrent below
      // will enrich it). Prevents chat from reconciling the previous tab's
      // thread id against the token another tab just wrote.
      yield put({
        type: 'user/saveCurrentUser',
        payload: {
          company: {
            id: companyId,
            name: companyName,
            commercialName: companyName,
          },
          ...(environmentId && {
            defaultEnvironment: { id: environmentId, name: environmentName },
          }),
        },
      });

      // Same tenant-scoped resets as a local switch — anything cached here
      // belongs to the outgoing company/environment.
      if (companyChanged) {
        resetWhiteLabelToDefault({ updateTitle: false });
        bootstrapAuthenticatedWhiteLabel({ updateTitle: false });
      }
      yield put({ type: 'chat/resetChatState' });
      yield put({ type: 'processes/resetLaunchpadHome' });
      yield put({ type: 'launchpad/resetAll' });
      yield put({ type: 'dashboard/reset' });
      yield put({ type: 'processes/saveFavoritesQuick', payload: [] });
      yield put({ type: 'channelOnboarding/invalidateOnboardingCache' });

      reconnectSocket();

      yield put({ type: 'user/fetchCurrent', payload: true });
      yield put({ type: 'fetchAllowedEnvironments', payload: { count: true } });
      yield put({ type: 'fetchAllowedSites' });
      yield put({ type: 'global/tasksCountPending' });
      yield put({ type: 'sessionNavigation/fetchNavigation' });

      // Whatever the tab was showing describes a record of the outgoing tenant.
      if (
        !getSessionItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS) &&
        !isInTwoFactorFlow() &&
        !isForeignShellRoute()
      ) {
        history.push('/');
      }

      notifyContextAdopted(
        companyChanged ? 'company' : 'environment',
        companyChanged ? companyName : environmentName,
      );
    },

    *changeEnvironment({ payload }, { call, put }) {
      try {
        const res = yield call(queryChangeEnvironment, payload);
        // Anything still in flight for the outgoing environment will be rejected.
        beginContextTransition();
        yield put({ type: 'changeContext', payload: { ...res, context: 'environment' } });

        // Update authority after reducer completes to avoid Redux violations
        try {
          const user = jwtDecode(res.accessToken);
          setAuthority(Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles));
        } catch (e) {}

        const authUser = readSessionAuthUser();
        if (authUser) {
          yield put({ type: 'user/saveCurrentUser', payload: authUser });
        }

        yield put({ type: 'chat/resetChatState' });
        // Same tenant-scoped resets as `changeCompany` — without these the
        // unified Launchpad slices keep stale items from the previous env.
        yield put({ type: 'processes/resetLaunchpadHome' });
        yield put({ type: 'launchpad/resetAll' });
        yield put({ type: 'dashboard/reset' });
        yield put({ type: 'processes/saveFavoritesQuick', payload: [] });
        yield put({ type: 'global/tasksCountPending' });
        yield put({ type: 'sessionNavigation/fetchNavigation' });
        yield put({ type: 'channelOnboarding/invalidateOnboardingCache' });

        reconnectSocket();
        yield put({ type: 'user/fetchCurrent', payload: true });

        payload.onFinish?.();
      } catch (e) {}
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *defaultContext({ payload }, { call, put, select }) {
      try {
        const storedContext = getSessionItem(STORAGE_KEYS.USER_CONTEXT);
        let {
          companyCount = 0,
          environmentCount = 0,
          siteCount = 0,
          agentCount,
          ...existingContext
        } = JSON.parse(storedContext || '{}', true);

        const isEmptyContext = !storedContext || (!companyCount && !environmentCount && !siteCount);
        const checked = yield select((st) => st.userContext.changeContext);

        if (!checked && isEmptyContext) {
          companyCount = yield call(queryCompanies, { count: true });
          environmentCount = yield call(queryAllowedEnvironments, { count: true });
          const sites = yield call(queryAllowedSites);

          siteCount = sites.totalCount;
          // Merge with new values, preserving agentCount if it exists
          const contextToSave = {
            ...existingContext,
            companyCount,
            environmentCount,
            siteCount,
            ...(agentCount !== undefined && { agentCount }),
          };

          setSessionItem(STORAGE_KEYS.USER_CONTEXT, JSON.stringify(contextToSave));
        }

        yield put({
          type: 'queryDefaultContext',
          payload: {
            ...payload,
            ...(!checked && {
              changeContext: {
                company: companyCount > 1,
                environment: environmentCount > 1,
                site: !!siteCount,
              },
            }),
          },
        });
      } catch (e) {}
    },

    *toggleSearchContext({ payload }, { put }) {
      yield put({ type: 'querySearchContext', payload });
    },

    *portalLogin({ payload }) {
      const path = buildPortalPath(payload?.companyCode, payload?.portalCode);
      if (!path) return;
      history.replace(path);
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    queryContextData(state, { payload }) {
      return {
        ...state,
        ...(!payload.context && { data: payload }),
        ...(payload.context && {
          // Switches also reload the portals; only the portal picker lists them.
          ...(payload.context === 'site' &&
            state.searchContext === 'site' && { data: payload.items }),
          changeContext: {
            ...state.changeContext,
            [payload.context]: payload.value,
          },
        }),
      };
    },

    changeContext(state, { payload }) {
      const { context: switchedContext, ...tokenPayload } = payload || {};
      const context = {};
      try {
        const user = jwtDecode(tokenPayload.accessToken);
        const authUser = JSON.parse(getSessionItem(STORAGE_KEYS.AUTH_USER) || 'null');
        const {
          companyId,
          companyCommercialName,
          companyName,
          environmentId,
          environmentName,
          positionId,
          positionName,
        } = user;

        // The effect names what it switched: the picker that started it may
        // have been closed while the request was out.
        switch (switchedContext || state.searchContext) {
          case 'company': {
            _.set(context, 'currentCompany', { id: companyId, name: companyName });
            _.set(authUser, 'position', { id: positionId, name: positionName });
            _.set(authUser, 'company', {
              id: companyId,
              commercialName: companyCommercialName,
              name: companyName,
            });
            // Environments belong to a company, so the token moved to one of
            // the incoming company's as well.
            if (environmentId) {
              _.set(context, 'currentEnvironment', { id: environmentId, name: environmentName });
              _.set(authUser, 'defaultEnvironment', { id: environmentId, name: environmentName });
            }
            break;
          }

          case 'environment':
            _.set(context, 'currentEnvironment', { id: environmentId, name: environmentName });
            _.set(authUser, 'defaultEnvironment', { id: environmentId, name: environmentName });
            break;

          default:
            break;
        }

        // Persist the profile that was just re-pointed at the new tenant. The
        // cache is shared with every other tab, and writing an empty object here
        // left any tab booting during the switch with no user at all.
        const session = getCurrentSession();
        if (
          authUser &&
          session?.type !== SESSION_TYPES.APP &&
          session?.type !== SESSION_TYPES.PORTAL
        ) {
          setSessionItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(authUser));
        }
        persistAutomyTenantToken(tokenPayload);
        // Its environment, portal and agent counts describe the tenant just
        // left; any tab booting from them would offer that tenant's pickers.
        removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
      } catch (e) {}

      return {
        ...state,
        ...context,
        searchCriteria: '',
      };
    },

    /**
     * Mirror a tenant adopted from another tab. Unlike `changeContext` this does
     * not touch storage: the token that triggered it is already the stored one.
     */
    adoptContext(state, { payload }) {
      const { companyId, companyName, environmentId, environmentName } = payload || {};

      return {
        ...state,
        ...(companyId && { currentCompany: { id: companyId, name: companyName } }),
        ...(environmentId && {
          currentEnvironment: { id: environmentId, name: environmentName },
        }),
        // The switcher was listing the outgoing tenant's siblings.
        searchContext: false,
        searchCriteria: '',
      };
    },

    setSearchCriteria(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    querySearchContext(state, { payload }) {
      return {
        ...state,
        searchContext: payload,
        searchCriteria: '',
      };
    },

    queryDefaultContext(state, { payload }) {
      const { changeContext, sites, ...user } = payload || {};
      return {
        ...state,
        ...(changeContext && { changeContext }),
        currentCompany: {
          id: user.companyId ?? user.company?.id,
          name: user.companyName ?? user.company?.name,
        },
        currentEnvironment: {
          id: user.environmentId ?? user.defaultEnvironment?.id,
          name: user.environmentName ?? user.defaultEnvironment?.name,
        },
        sites,
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
      };
    },

    reset() {
      return initialState;
    },
  },
  subscriptions: {
    contextSync({ dispatch }) {
      return subscribeToSessionContextChange((context) => {
        dispatch({ type: 'adoptExternalContext', payload: context });
      });
    },
  },
};

export default Model;
